import {
  FileInput,
  InputTriggerProps,
  Tag,
  TextBody,
  TextLabel,
  baseButtonVariants,
  cn,
  cva,
} from "@sourceful/design-system-v3";
import React from "react";
import env from "../../../lib/utils/env";

const MAX_FILE_SIZE_UPLOAD = env("MAX_FILE_SIZE_UPLOAD");

export interface LeadGenFormFileUploaderProps {
  heading: string;
  subheading: string;
  onFiles: (files: File[] | null) => void;
  initialFiles?: File[];
}

const browseButtonStyles = cva(
  `flex justify-between items-stretch border border-ui-grey-300 p-0 cursor-pointer`,
  {
    variants: {
      error: {
        true: "border !border-ui-red-700 [&[data-state=checked]]:bg-ui-red-700 [&[data-state=checked]]:focus:!border-ui-red-700 focus:!outline-ui-red-400/50",
      },
    },
  }
);

const bytesToMb = (bytes: number) => {
  return (bytes / 1024 / 1024).toFixed(2);
};

const renderInputTrigger = ({ inputRef, files, error }: InputTriggerProps) => {
  return (
    <div className="flex flex-col gap-24 bg-ui-lightest">
      <button
        type="button"
        tabIndex={0}
        className={cn(baseButtonVariants({ size: "md" }), browseButtonStyles({ error }))}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        <span className="flex items-center px-16">Files: {files?.length ?? 0}</span>
        <span className="flex items-center border-l border-inherit px-16">Browse</span>
      </button>
    </div>
  );
};

export default function LeadGenFormFileUploader({
  heading,
  subheading,
  onFiles,
  initialFiles = [],
}: Readonly<LeadGenFormFileUploaderProps>) {
  const [files, setFiles] = React.useState<File[]>(initialFiles);

  React.useEffect(() => {
    onFiles(files);
  }, [files, onFiles]);

  const onInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const filesLocal = [...files];
      const chosenFiles = Array.prototype.slice.call(event.target.files as FileList);
      chosenFiles.forEach((file: File) => {
        if (filesLocal.findIndex((f: File) => f.name === file.name) === -1) {
          filesLocal.push(file);
        }
      });
      setFiles(filesLocal);

      if (event.target) {
        event.target.value = "";
      }
    },
    [files]
  );

  const error = React.useMemo(() => {
    const uploadSize = files.reduce((acc, file) => acc + file.size, 0);
    return uploadSize > MAX_FILE_SIZE_UPLOAD;
  }, [files]);

  const renderFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      return (
        <div className="flex flex-wrap gap-16 mt-12">
          {files.map(file => (
            <Tag
              key={file.name}
              variant={"fill"}
              color={"blue"}
              text={`${file.name} (${bytesToMb(file.size)}MB)`}
              close
              error={error}
              onClose={() => {
                setFiles(files.filter((f: File) => f.name !== file.name));
              }}
            />
          ))}
        </div>
      );
    },
    [error]
  );

  return (
    <div className="bg-ui-grey-50 p-8 rounded-sm lg:p-20 flex flex-col gap-8 lg:gap-12">
      <TextBody size="small" weight={"medium"} asChild>
        <h3>{heading}</h3>
      </TextBody>
      <TextLabel size="large" weight="medium" asChild>
        <label htmlFor="pdp-lead-upload">{subheading}</label>
      </TextLabel>
      <FileInput
        multiple
        onChange={onInputChange}
        renderInputTrigger={renderInputTrigger}
        renderFiles={renderFiles}
        files={files}
        id="pdp-lead-upload"
      />
      {error && (
        <TextLabel size="medium" asChild>
          <span className="text-ui-red-700">
            Total file size exceeds {bytesToMb(MAX_FILE_SIZE_UPLOAD)}MB
          </span>
        </TextLabel>
      )}
    </div>
  );
}
